import {
  INTRANET_GROUP_CHAT_ALL_TEAMS_MESSAGE,
  INTRANET_GROUP_CHAT_MIN_MEMBERS,
  INTRANET_GROUP_CHAT_NAME_MAX,
  createIntranetGroupChatRequestSchema,
  validateIntranetGroupChatLicenses,
} from '@gb-ticket/shared-types';
import { Loader2Icon, UsersIcon } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiRequestError } from '@/lib/api';
import { useOwnProfile } from '@/platform/features/users/use-own-profile';
import { useCreateIntranetGroupChat } from '@/products/intranet/features/intranet/use-intranet-chat';
import { useIntranetColleagues } from '@/products/intranet/features/intranet/use-intranet-directory';
import { intranetChatPath } from '@/products/intranet/intranet-paths';

export function CreateGroupChatDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateIntranetGroupChat();
  const profile = useOwnProfile();
  const [name, setName] = useState('');
  const [personQuery, setPersonQuery] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const colleagues = useIntranetColleagues({ q: personQuery || undefined, pageSize: 100 }, open);

  const actorHasMicrosoft = Boolean(profile.data?.entraObjectId?.trim());
  const selectedPeople = useMemo(() => {
    const byId = new Map(
      (colleagues.data?.items ?? []).map(
        (person) =>
          [
            person.id,
            { name: person.displayName, hasMicrosoftAccount: person.hasMicrosoftAccount },
          ] as const,
      ),
    );
    return memberIds.map((id) => ({
      id,
      name: byId.get(id)?.name ?? 'Ausgewählte Person',
      hasMicrosoftAccount: byId.get(id)?.hasMicrosoftAccount ?? true,
    }));
  }, [colleagues.data?.items, memberIds]);

  const licenseHint = useMemo(() => {
    if (!profile.data || memberIds.length < INTRANET_GROUP_CHAT_MIN_MEMBERS - 1) {
      return null;
    }
    const check = validateIntranetGroupChatLicenses([
      actorHasMicrosoft,
      ...selectedPeople.map((person) => person.hasMicrosoftAccount),
    ]);
    return check.ok
      ? null
      : check.code === 'ALL_TEAMS'
        ? INTRANET_GROUP_CHAT_ALL_TEAMS_MESSAGE
        : null;
  }, [actorHasMicrosoft, memberIds.length, profile.data, selectedPeople]);

  function close() {
    setName('');
    setPersonQuery('');
    setMemberIds([]);
    onClose();
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = createIntranetGroupChatRequestSchema.safeParse({
      name,
      memberIds,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Bitte die Angaben prüfen.');
      return;
    }
    if (licenseHint) {
      toast.error(licenseHint);
      return;
    }
    if (profile.data) {
      const check = validateIntranetGroupChatLicenses([
        actorHasMicrosoft,
        ...selectedPeople.map((person) => person.hasMicrosoftAccount),
      ]);
      if (!check.ok && check.code === 'ALL_TEAMS') {
        toast.error(INTRANET_GROUP_CHAT_ALL_TEAMS_MESSAGE);
        return;
      }
    }
    try {
      const result = await create.mutateAsync(parsed.data);
      toast.success('Gruppenchat angelegt.');
      close();
      void navigate(intranetChatPath(result.conversationId));
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Gruppenchat konnte nicht angelegt werden.',
      );
    }
  }

  const selectable = (colleagues.data?.items ?? []).filter(
    (person) => person.id !== profile.data?.id,
  );

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent className="max-h-[min(40rem,90vh)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersIcon className="size-4" aria-hidden />
            Gruppenchat anlegen
          </DialogTitle>
          <DialogDescription>
            Mindestens {INTRANET_GROUP_CHAT_MIN_MEMBERS} Personen, davon mindestens eine ohne
            Teams-Lizenz. Reine Teams-Gruppen bitte in Microsoft Teams führen.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(event) => void onSubmit(event)}>
          <div className="space-y-1.5">
            <Label htmlFor="group-chat-name">Gruppenname</Label>
            <Input
              id="group-chat-name"
              value={name}
              maxLength={INTRANET_GROUP_CHAT_NAME_MAX}
              placeholder="z. B. Azubi-Frühstück"
              onChange={(event) => setName(event.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="group-chat-search">Teilnehmer</Label>
            <Input
              id="group-chat-search"
              value={personQuery}
              placeholder="Name suchen …"
              onChange={(event) => setPersonQuery(event.target.value)}
              autoComplete="off"
            />
            {selectedPeople.length > 0 ? (
              <ul className="flex flex-wrap gap-2 text-xs">
                {selectedPeople.map((person) => (
                  <li key={person.id}>
                    <button
                      type="button"
                      className="rounded-md border px-2 py-1 hover:bg-muted"
                      onClick={() => setMemberIds((ids) => ids.filter((id) => id !== person.id))}
                    >
                      {person.name} ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-xl border border-border p-2">
              {colleagues.isPending ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Personen werden geladen …
                </p>
              ) : null}
              {selectable.map((person) => (
                <label
                  key={person.id}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm"
                >
                  <Checkbox
                    checked={memberIds.includes(person.id)}
                    onCheckedChange={(checked) =>
                      setMemberIds((ids) =>
                        checked === true
                          ? [...new Set([...ids, person.id])]
                          : ids.filter((id) => id !== person.id),
                      )
                    }
                  />
                  <span>
                    <span className="font-medium">{person.displayName}</span>
                    <span className="block text-xs text-muted-foreground">
                      {person.hasMicrosoftAccount ? 'Teams-Lizenz' : 'Lokal (ohne Teams)'}
                      {person.departmentName ? ` · ${person.departmentName}` : ''}
                    </span>
                  </span>
                </label>
              ))}
              {!colleagues.isPending && selectable.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Personen gefunden.</p>
              ) : null}
            </div>
          </div>

          {licenseHint ? (
            <p role="alert" className="text-sm text-destructive">
              {licenseHint}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={create.isPending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={create.isPending || Boolean(licenseHint)}>
              {create.isPending ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
              ) : (
                <UsersIcon className="size-4" aria-hidden />
              )}
              Gruppe anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
